'use client'

import type { ReactNode } from 'react'

export type MediaPosition = 'top' | 'left' | 'background'

export type PopupContentProps = {
  headline: string | null
  body: string | null
  ctaLabel: string | null
  ctaHref: string | null
  secondaryCtaLabel?: string | null
  secondaryCtaHref?: string | null
  imageUrl: string | null
  imageAlt?: string | null
  /**
   * 'top' → image above the headline (default).
   * 'left' → image sits in a side column next to the content (modal only).
   * 'background' → image rendered by the shell as a backdrop; content sits
   *   on top with a darker text treatment for legibility.
   */
  mediaPosition?: MediaPosition
  onCtaClick?: () => void
  /** Secondary click handler; defaults to onCtaClick. Used by "No thanks" links. */
  onSecondaryClick?: () => void
  /**
   * Compact rendering removes the image and trims padding/scale — used by
   * the bottom-bar layout where vertical space is tight.
   */
  compact?: boolean
  headlineId?: string
}

export function PopupContent({
  headline,
  body,
  ctaLabel,
  ctaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  imageUrl,
  imageAlt,
  mediaPosition = 'top',
  onCtaClick,
  onSecondaryClick,
  compact = false,
  headlineId,
}: PopupContentProps) {
  const headlineNode: ReactNode = headline ? (
    <h2
      id={headlineId}
      className={compact ? 'text-base font-semibold' : 'text-2xl font-semibold leading-tight'}
      style={{ fontFamily: 'var(--popup-headline-font)' }}
    >
      {headline}
    </h2>
  ) : null

  const bodyNode: ReactNode = body ? (
    <p className={compact ? 'text-sm opacity-90' : 'text-base opacity-90 leading-relaxed'}>{body}</p>
  ) : null

  const ctaNode: ReactNode =
    ctaLabel && ctaHref ? (
      <a
        href={ctaHref}
        onClick={onCtaClick}
        className={
          (compact
            ? 'inline-flex items-center justify-center px-3 py-1.5 text-sm '
            : 'inline-flex items-center justify-center px-5 py-2.5 text-base ') +
          'font-medium rounded-md transition-opacity hover:opacity-90'
        }
        style={{
          background: 'var(--popup-accent)',
          color: 'var(--popup-accent-text)',
        }}
      >
        {ctaLabel}
      </a>
    ) : null

  // Secondary CTA — when href is null and label is set, it acts as a
  // dismiss link. When both set, it's a real link styled as a text button.
  const secondaryCtaNode: ReactNode = secondaryCtaLabel ? (
    secondaryCtaHref ? (
      <a
        href={secondaryCtaHref}
        onClick={onSecondaryClick}
        className="inline-flex items-center text-sm opacity-70 hover:opacity-100 underline underline-offset-2 transition-opacity"
        style={{ color: 'currentColor' }}
      >
        {secondaryCtaLabel}
      </a>
    ) : (
      <button
        type="button"
        onClick={onSecondaryClick}
        className="inline-flex items-center text-sm opacity-70 hover:opacity-100 underline underline-offset-2 transition-opacity"
        style={{ background: 'none', color: 'currentColor', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        {secondaryCtaLabel}
      </button>
    )
  ) : null

  const ctaCluster =
    ctaNode || secondaryCtaNode ? (
      <div className={compact ? 'flex items-center gap-3' : 'flex items-center gap-4 pt-1'}>
        {ctaNode}
        {secondaryCtaNode}
      </div>
    ) : null

  if (compact) {
    return (
      <div className="flex items-center gap-4 w-full">
        <div className="flex-1 min-w-0 flex items-baseline gap-3">
          {headlineNode}
          {bodyNode && <span className="hidden sm:inline">·</span>}
          {bodyNode}
        </div>
        {ctaCluster}
      </div>
    )
  }

  // background mode: the shell paints the image as a backdrop. Content sits
  // on top; we add a light text-shadow on the headline for legibility.
  if (mediaPosition === 'background') {
    return (
      <div className="flex flex-col items-stretch gap-4 relative">
        {headline && (
          <h2
            id={headlineId}
            className="text-2xl font-semibold leading-tight text-white"
            style={{ fontFamily: 'var(--popup-headline-font)', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}
          >
            {headline}
          </h2>
        )}
        {body && (
          <p
            className="text-base text-white/90 leading-relaxed"
            style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}
          >
            {body}
          </p>
        )}
        {ctaCluster}
      </div>
    )
  }

  // left mode: image column on the left, content column on the right.
  // Falls back to top-mode on narrow viewports via Tailwind responsive cues.
  if (mediaPosition === 'left' && imageUrl) {
    return (
      <div className="flex flex-col sm:flex-row gap-5 items-stretch">
        <div className="sm:w-2/5 -m-7 sm:my-[-1.75rem] sm:ml-[-1.75rem] sm:mr-0 overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt={imageAlt ?? ''}
            className="w-full h-full object-cover min-h-[180px]"
          />
        </div>
        <div className="flex-1 flex flex-col gap-4 justify-center">
          {headlineNode}
          {bodyNode}
          {ctaCluster}
        </div>
      </div>
    )
  }

  // default: top
  return (
    <div className="flex flex-col items-stretch gap-4">
      {imageUrl && (
        <div className="overflow-hidden rounded-md -mx-1">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt={imageAlt ?? ''}
            className="w-full h-auto object-cover"
            style={{ maxHeight: '200px' }}
          />
        </div>
      )}
      {headlineNode}
      {bodyNode}
      {ctaCluster}
    </div>
  )
}
